import * as React from 'react'
import { ColumnDef } from '@tanstack/react-table'
import { DataTable } from '@/components/DataTable'
import { Link } from 'react-router-dom'
import { Building2, Link as LinkIcon, MoreHorizontal, NotebookText, Store } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Badge } from '@/components/ui/badge'
import { Subscription } from '@/features/accounts/api/get-subscriptions'
import SubscriptionsTableFilter from './SubscriptionsTableFilter'
import { format, parseISO, isValid, isAfter, isBefore, isEqual } from 'date-fns'

const formatCentsToUSD = (value: unknown) => {
    const cents = Number(value)
    if (!Number.isFinite(cents)) return '—'
    const dollars = cents / 100
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(dollars)
}

const formatMaybeDate = (value: unknown, fmt: string) => {
    if (!value) return ''
    let date: Date
    if (value instanceof Date) {
        date = value
    } else if (typeof value === 'string') {
        const parsed = parseISO(value)
        if (!isValid(parsed)) return ''
        date = parsed
    } else {
        // attempt to construct
        const constructed = new Date(value as any)
        if (!isValid(constructed)) return ''
        date = constructed
    }
    return format(date, fmt)
}

const columns: ColumnDef<Subscription>[] = [
    {
        accessorKey: 'scope',
        header: 'Subscriber',
        cell: ({ row }) => {
            const sub = row.original
            return sub.scope === 'account' ? (
                <Link to={`/accounts/${sub.accountId}`} className='text-primary hover:underline flex items-center gap-1'>
                    <Badge variant='outline'><Building2 className='h-3 w-3 mr-1' /> Account</Badge>
                    {sub.account?.name ?? '—'}
                </Link>
            ) : (
                <Link to={`/accounts/${sub.accountId}`} className='text-primary hover:underline flex items-center gap-1'>
                    <Badge variant='outline'><Store className='h-3 w-3 mr-1' /> Store</Badge>
                    {sub.store?.name}
                </Link>
            )
        },
        footer: ({ table }) => {
            const meta = table.options.meta as any
            const totalItems = typeof meta?.totalItems === 'number' ? meta.totalItems : table.getRowModel().rows.length
            return `Count: ${totalItems}`
        },
    },
    {
        accessorKey: 'planId',
        header: 'Plan',
        cell: ({ row }) => {
            const firstItem = Array.isArray(row.original.items) ? row.original.items[0] : undefined
            const plan = firstItem?.plan
            if (!plan) return (<span className='font-medium italic'>No plan</span>)
            return (
                <Link
                    to={`/plans/${plan.planId}`}
                    className='text-primary hover:text-primary/80 flex items-center gap-1 cursor-pointer'
                    aria-label={`Open plan ${plan.planId}`}
                >

                    <NotebookText className='h-3 w-3 mr-1' />
                    {plan?.name}

                    <LinkIcon className='h-3 w-3' />
                </Link>
            )
        },
    },
    {
        accessorKey: 'recurringAmount',
        header: 'Recurring Amount',
        cell: ({ row }) => {
            const formatted = formatCentsToUSD(row.getValue('recurringAmount'))
            return <span>{formatted}</span>
        },
        footer: ({ table }) => {
            const meta = table.options.meta as any
            const annualCents = meta?.totals?.recurringAnnualAmount ?? 0
            const monthlyCents = meta?.totals?.recurringMonthlyAmount ?? 0
            const totalCents = annualCents + monthlyCents
            
            if (typeof annualCents === 'number' || typeof monthlyCents === 'number') {
                return `Total: ${formatCentsToUSD(totalCents)}`
            }
            const clientTotalCents = table.getRowModel().rows.reduce((sum, r) => {
                const v = Number(r.getValue('recurringAmount')) || 0
                return sum + v
            }, 0)
            return `Total: ${formatCentsToUSD(clientTotalCents)}`
        },
    },
    {
        id: 'billing',
        header: 'Billing',
        cell: ({ row }) => {
            const sub = row.original
            const source = sub.chargebeeSubscriptionId ? 'direct' : 'fastbound'
            return (
                <Badge variant='outline' className='w-fit capitalize'>{source}</Badge>
            )
        },
    },
    {
        accessorKey: 'renewalAt',
        header: 'Next Renewal',
        cell: ({ row }) => {
            const formatted = formatMaybeDate(row.getValue('renewalAt'), 'MM/dd/yyyy')
            return <span className='text-muted-foreground'>{formatted}</span>
        },
    },

    {
        id: 'actions',
        enableHiding: false,
        cell: ({ row }) => {
            const account = row.original
            return (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant='ghost' className='h-8 w-8 p-0'>
                            <span className='sr-only'>Open menu</span>
                            <MoreHorizontal />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align='end'>
                        <DropdownMenuLabel>Billing</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem>
                            <Link to={`/subscriptions/${account.subscriptionId}`}>
                                View
                            </Link>
                        </DropdownMenuItem>
                    </DropdownMenuContent>
                </DropdownMenu>
            )
        },
    },
]

type SubscriptionsTableProps = {
    data: Subscription[]
    isLoading?: boolean
    pageIndex: number
    pageSize: number
    pageCount: number
    showRowCountColumn?: boolean
    onPaginationChange: (updater: any) => void
    sorting: Array<{ id: string; desc: boolean }>
    onSortingChange: (updater: any) => void
    accountId: string
    onAccountChange: (v: string) => void
    scope: 'all' | 'account' | 'store'
    onScopeChange: (v: 'all' | 'account' | 'store') => void
    renewalRange: { from: Date | null; to: Date | null }
    onRenewalRangeChange: (v: { from: Date | null; to: Date | null }) => void
    search: string
    onSearchChange: (v: string) => void
    billing?: 'all' | 'direct' | 'fastbound'
    onBillingChange?: (v: 'all' | 'direct' | 'fastbound') => void
    totals?: { count: number; recurringAnnualAmount: number; recurringMonthlyAmount: number } | undefined
    hideFilters?: boolean
    totalItems?: number
}

export default function SubscriptionsTable({ data, isLoading, pageIndex, pageSize, pageCount, showRowCountColumn, onPaginationChange, sorting, onSortingChange, accountId, onAccountChange, scope, onScopeChange, renewalRange, onRenewalRangeChange, search, onSearchChange, billing, onBillingChange, totals, hideFilters, totalItems }: SubscriptionsTableProps) {
    return (
        <div className='w-full'>
            {!hideFilters && (
                <SubscriptionsTableFilter
                    accountId={accountId}
                    onAccountChange={onAccountChange}
                    scope={scope}
                    onScopeChange={onScopeChange}
                    renewalRange={renewalRange}
                    onRenewalRangeChange={onRenewalRangeChange}
                    search={search}
                    onSearchChange={onSearchChange}
                    billing={billing}
                    onBillingChange={onBillingChange}
                />
            )}
            <DataTable
                columns={columns}
                data={data}
                isLoading={isLoading}
                enablePagination={true}
                serverPagination={true}
                pageIndex={pageIndex}
                pageSize={pageSize}
                pageCount={pageCount}
                onPaginationChange={onPaginationChange}
                serverSorting={true}
                sorting={sorting}
                onSortingChange={onSortingChange}
                meta={{ totals, totalItems }}
                scrollContainerClassName='max-h-[calc(100vh-500px)] overflow-y-auto'
                rowCountColumn={showRowCountColumn}
            />
        </div>
    )
}


